import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Badge } from "components/ui/badge";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useExtendedDistributions } from "../hooks/useStatisticsData";
import { MONTHS } from "../constants/period";
import { ExtendedDistributionFilters, CategoryCount } from "../types/statistics.types";
import { Building, Tag, Users, MapPin, Cube } from "@phosphor-icons/react";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Color palette for charts
const COLORS = [
  "#EA8162",
  "#5B7304",
  "#31403B",
  "#F59E0B",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#F97316",
  "#6366F1",
  "#84CC16",
  "#06B6D4",
  "#A855F7",
  "#F43F5E",
  "#22C55E",
];

const COLORS_ALPHA = COLORS.map((c) => `${c}CC`);

// Shared filter controls component
function DistributionFilters({
  filters,
  setFilters,
  availableYears,
  totalTickets,
}: {
  filters: ExtendedDistributionFilters;
  setFilters: React.Dispatch<React.SetStateAction<ExtendedDistributionFilters>>;
  availableYears?: number[];
  totalTickets: number;
}) {
  const getFilterDisplayText = () => {
    const parts = [];
    if (filters.ticketStatus === "open") parts.push("Open tickets");
    else if (filters.ticketStatus === "closed") parts.push("Gesloten tickets");
    else parts.push("Alle tickets");

    if (filters.year !== "all") {
      if (filters.month !== "all") {
        const monthLabel = MONTHS.find(m => m.value === filters.month)?.label;
        parts.push(`${monthLabel} ${filters.year}`);
      } else {
        parts.push(`Jaar ${filters.year}`);
      }
    }

    return parts.join(" - ");
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
      <div>
        <p className="text-sm text-muted-foreground">
          {getFilterDisplayText()} - {totalTickets} tickets totaal
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={filters.ticketStatus}
          onValueChange={(value: 'open' | 'closed' | 'all') =>
            setFilters(prev => ({ ...prev, ticketStatus: value }))
          }
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="closed">Gesloten</SelectItem>
            <SelectItem value="all">Alle</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.year}
          onValueChange={(value) =>
            setFilters(prev => ({ ...prev, year: value }))
          }
        >
          <SelectTrigger className="w-[110px]">
            <SelectValue placeholder="Jaar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle jaren</SelectItem>
            {availableYears?.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filters.year !== "all" && (
          <Select
            value={filters.month}
            onValueChange={(value) =>
              setFilters(prev => ({ ...prev, month: value }))
            }
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Maand" />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((month) => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

// Bar chart options
const createBarOptions = (data: any, dataKey: 'byBuilding' | 'byLabel' | 'bySource') => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y' as const,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: "#31403B",
      titleColor: "#F9F0EE",
      bodyColor: "#F9F0EE",
      borderColor: "#EA8162",
      borderWidth: 1,
      padding: 12,
      callbacks: {
        label: (context: any) => {
          const value = context.parsed.x;
          const index = context.dataIndex;
          const originalData = data?.[dataKey]?.[index];
          if (originalData && 'avgDoorlooptijd' in originalData) {
            return [
              `Tickets: ${value}`,
              `Gem. doorlooptijd: ${originalData.avgDoorlooptijd}d`,
            ];
          }
          return `Tickets: ${value}`;
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        color: "rgba(49, 64, 59, 0.1)",
      },
    },
    y: {
      grid: {
        display: false,
      },
    },
  },
});

// Building Distribution Chart
export function BuildingDistributionChart() {
  const [filters, setFilters] = useState<ExtendedDistributionFilters>({
    ticketStatus: "open",
    year: "all",
    month: "all",
  });

  const { data, loading } = useExtendedDistributions(filters);

  const chartData = useMemo(() => {
    if (!data?.byBuilding) return null;

    const buildings = data.byBuilding.slice(0, 12);

    return {
      labels: buildings.map((b) =>
        b.buildingName.length > 20
          ? b.buildingName.substring(0, 20) + "..."
          : b.buildingName
      ),
      datasets: [
        {
          label: "Tickets",
          data: buildings.map((b) => b.count),
          backgroundColor: COLORS_ALPHA.slice(0, buildings.length),
          borderColor: COLORS.slice(0, buildings.length),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [data]);

  const totalTickets = useMemo(() => {
    if (!data?.byBuilding) return 0;
    return data.byBuilding.reduce((sum, b) => sum + b.count, 0);
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-primary" />
            <Skeleton className="h-5 w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[350px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Building className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Tickets per Gebouw</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <DistributionFilters
          filters={filters}
          setFilters={setFilters}
          availableYears={data?.availableYears}
          totalTickets={totalTickets}
        />
        <div className="h-[350px]">
          {chartData ? (
            <Bar data={chartData} options={createBarOptions(data, 'byBuilding')} />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Category Distribution Chart
export function CategoryDistributionChart() {
  const [filters, setFilters] = useState<ExtendedDistributionFilters>({
    ticketStatus: "open",
    year: "all",
    month: "all",
  });

  const { data, loading } = useExtendedDistributions(filters);

  const chartData = useMemo(() => {
    if (!data?.byLabel) return null;

    const labels = data.byLabel.slice(0, 12);

    return {
      labels: labels.map((l) =>
        l.label.length > 20
          ? l.label.substring(0, 20) + "..."
          : l.label
      ),
      datasets: [
        {
          label: "Tickets",
          data: labels.map((l) => l.count),
          backgroundColor: COLORS_ALPHA.slice(0, labels.length),
          borderColor: COLORS.slice(0, labels.length),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [data]);

  const totalTickets = useMemo(() => {
    if (!data?.byLabel) return 0;
    return data.byLabel.reduce((sum, l) => sum + l.count, 0);
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            <Skeleton className="h-5 w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[350px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Tickets per Categorie</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <DistributionFilters
          filters={filters}
          setFilters={setFilters}
          availableYears={data?.availableYears}
          totalTickets={totalTickets}
        />
        <div className="h-[350px]">
          {chartData ? (
            <Bar data={chartData} options={createBarOptions(data, 'byLabel')} />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Source Distribution Chart (now bar chart)
export function SourceDistributionChart() {
  const [filters, setFilters] = useState<ExtendedDistributionFilters>({
    ticketStatus: "open",
    year: "all",
    month: "all",
  });

  const { data, loading } = useExtendedDistributions(filters);

  const chartData = useMemo(() => {
    if (!data?.bySource) return null;

    return {
      labels: data.bySource.map((s) => s.source),
      datasets: [
        {
          label: "Tickets",
          data: data.bySource.map((s) => s.count),
          backgroundColor: COLORS_ALPHA.slice(0, data.bySource.length),
          borderColor: COLORS.slice(0, data.bySource.length),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [data]);

  const totalTickets = useMemo(() => {
    if (!data?.bySource) return 0;
    return data.bySource.reduce((sum, s) => sum + s.count, 0);
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <Skeleton className="h-5 w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Tickets per Bron</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <DistributionFilters
          filters={filters}
          setFilters={setFilters}
          availableYears={data?.availableYears}
          totalTickets={totalTickets}
        />
        <div className="h-[200px]">
          {chartData ? (
            <Bar data={chartData} options={createBarOptions(data, 'bySource')} />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Supplier Dispatch View - Category-focused building/cluster analysis
export function ClusterBuildingAnalysis() {
  const [filters, setFilters] = useState<ExtendedDistributionFilters>({
    ticketStatus: "open",
    year: "all",
    month: "all",
  });
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"building" | "cluster">("building");
  const [searchQuery, setSearchQuery] = useState("");

  const { data, loading } = useExtendedDistributions(filters);
  const analysis = data?.analysis;

  const navigate = useNavigate();

  // Build flat list of all buildings with their cluster info
  const allBuildings = useMemo(() => {
    if (!analysis) return [];

    const buildings: Array<{
      buildingId: string;
      clusterName: string | null;
      clusterId: number | null;
      categories: CategoryCount[];
      totalTickets: number;
    }> = [];

    // Add buildings from clusters
    analysis.clusters.forEach((cluster) => {
      cluster.buildings.forEach((building) => {
        buildings.push({
          buildingId: building.buildingId,
          clusterName: cluster.clusterName,
          clusterId: cluster.clusterId,
          categories: building.categories,
          totalTickets: building.totalTickets,
        });
      });
    });

    // Add unclustered buildings
    analysis.unclustered.forEach((building) => {
      buildings.push({
        buildingId: building.buildingId,
        clusterName: null,
        clusterId: null,
        categories: building.categories,
        totalTickets: building.totalTickets,
      });
    });

    return buildings;
  }, [analysis]);

  // Get ticket count for a specific category (or all)
  const getCategoryCount = (categories: CategoryCount[], category: string) => {
    if (category === "all") {
      return categories.reduce((sum, c) => sum + c.count, 0);
    }
    return categories.find((c) => c.label === category)?.count || 0;
  };

  // Filter and sort buildings
  const filteredBuildings = useMemo(() => {
    let result = allBuildings;

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          b.buildingId.toLowerCase().includes(query) ||
          (b.clusterName && b.clusterName.toLowerCase().includes(query))
      );
    }

    // Filter out buildings with 0 tickets for selected category
    result = result.filter(
      (b) => getCategoryCount(b.categories, selectedCategory) > 0
    );

    // Sort by ticket count for selected category
    result = result.sort(
      (a, b) =>
        getCategoryCount(b.categories, selectedCategory) -
        getCategoryCount(a.categories, selectedCategory)
    );

    return result;
  }, [allBuildings, searchQuery, selectedCategory]);

  // Aggregate by cluster
  const clusterAggregates = useMemo(() => {
    if (!analysis) return [];

    const clusters = analysis.clusters.map((cluster) => {
      const categorySum =
        selectedCategory === "all"
          ? cluster.totalTickets
          : cluster.categories.find((c) => c.label === selectedCategory)
              ?.count || 0;

      return {
        clusterId: cluster.clusterId,
        clusterName: cluster.clusterName,
        buildingCount: cluster.buildings.length,
        ticketCount: categorySum,
        categories: cluster.categories,
      };
    });

    // Filter and sort
    return clusters
      .filter((c) => c.ticketCount > 0)
      .sort((a, b) => b.ticketCount - a.ticketCount);
  }, [analysis, selectedCategory]);

  const totalTickets = useMemo(() => {
    if (!data?.byBuilding) return 0;
    return data.byBuilding.reduce((sum, b) => sum + b.count, 0);
  }, [data]);

  // Get color for category
  const getCategoryColor = (category: string) => {
    if (!analysis?.allCategories) return COLORS[0];
    const index = analysis.allCategories.indexOf(category);
    return COLORS[index >= 0 ? index % COLORS.length : 0];
  };

  // Navigate to helpdesk with filters
  const handleBuildingClick = (buildingId: string) => {
    const params = new URLSearchParams();
    params.set("buildingId", buildingId);
    if (selectedCategory !== "all") {
      params.set("label", selectedCategory);
    }
    navigate(`/helpdesk?${params.toString()}`);
  };

  const handleClusterClick = (clusterId: number) => {
    // Find all building IDs in this cluster
    const cluster = analysis?.clusters.find((c) => c.clusterId === clusterId);
    if (cluster && cluster.buildings.length > 0) {
      // Navigate to first building or show cluster view
      handleBuildingClick(cluster.buildings[0].buildingId);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <Skeleton className="h-5 w-48" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">
              Waar stuur ik mijn leverancier?
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
            <p>Geen data beschikbaar</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedCategoryLabel =
    selectedCategory === "all"
      ? "Alle categorieën"
      : selectedCategory;
  const selectedCategoryCount =
    selectedCategory === "all"
      ? totalTickets
      : analysis.totals.byCategory.find((c) => c.label === selectedCategory)
          ?.count || 0;

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" weight="duotone" />
          <CardTitle className="text-lg">
            Waar stuur ik mijn leverancier?
          </CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Selecteer een categorie om te zien waar je leveranciers moet inzetten
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters row */}
        <DistributionFilters
          filters={filters}
          setFilters={setFilters}
          availableYears={data?.availableYears}
          totalTickets={totalTickets}
        />

        {/* Category selector + View mode + Search */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {/* Category dropdown */}
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="w-[200px]">
                <div className="flex items-center gap-2">
                  {selectedCategory !== "all" && (
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{
                        backgroundColor: getCategoryColor(selectedCategory),
                      }}
                    />
                  )}
                  <SelectValue placeholder="Selecteer categorie" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-medium">Alle categorieën</span>
                </SelectItem>
                {analysis.totals.byCategory.map((cat) => (
                  <SelectItem key={cat.label} value={cat.label}>
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: getCategoryColor(cat.label) }}
                      />
                      <span>{cat.label}</span>
                      <span className="text-muted-foreground">({cat.count})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* View mode toggle */}
            <div className="flex rounded-lg border bg-muted/30 p-0.5">
              <button
                onClick={() => setViewMode("building")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  viewMode === "building"
                    ? "bg-white shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Cube className="h-4 w-4" />
                Gebouwen
              </button>
              <button
                onClick={() => setViewMode("cluster")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  viewMode === "cluster"
                    ? "bg-white shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <MapPin className="h-4 w-4" />
                Clusters
              </button>
            </div>
          </div>

          {/* Search (only for buildings) */}
          {viewMode === "building" && (
            <div className="relative w-full sm:w-[250px]">
              <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Zoek gebouw of cluster..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
        </div>

        {/* Selected category summary */}
        <div className="flex items-center justify-between rounded-lg bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-3">
            {selectedCategory !== "all" && (
              <span
                className="h-4 w-4 rounded-full"
                style={{ backgroundColor: getCategoryColor(selectedCategory) }}
              />
            )}
            <div>
              <span className="font-medium">{selectedCategoryLabel}</span>
              <span className="text-muted-foreground ml-2">
                {selectedCategoryCount} open ticket
                {selectedCategoryCount !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
          {viewMode === "building" && (
            <Badge variant="outline">
              {filteredBuildings.length} gebouw
              {filteredBuildings.length !== 1 ? "en" : ""}
            </Badge>
          )}
          {viewMode === "cluster" && (
            <Badge variant="outline">
              {clusterAggregates.length} cluster
              {clusterAggregates.length !== 1 ? "s" : ""}
            </Badge>
          )}
        </div>

        {/* Results table */}
        <div className="rounded-lg border">
          {viewMode === "building" ? (
            // Building view
            <div className="max-h-[450px] overflow-y-auto">
              {filteredBuildings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Cube className="h-10 w-10 mb-2 opacity-50" />
                  <p>Geen gebouwen gevonden</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="sticky top-0 bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground w-12">
                        #
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                        Gebouw
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                        Cluster
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">
                        Tickets
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBuildings.map((building, index) => {
                      const count = getCategoryCount(
                        building.categories,
                        selectedCategory
                      );
                      return (
                        <tr
                          key={building.buildingId}
                          onClick={() => handleBuildingClick(building.buildingId)}
                          className="hover:bg-muted/30 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-2.5 text-sm text-muted-foreground font-medium">
                            {index + 1}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <Cube
                                className="h-4 w-4 text-muted-foreground"
                                weight="duotone"
                              />
                              <span className="text-sm font-medium">
                                {building.buildingId}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-muted-foreground">
                            {building.clusterName || "-"}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Badge
                              variant={count >= 5 ? "destructive" : "secondary"}
                              className="font-mono"
                            >
                              {count}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            // Cluster view
            <div className="max-h-[450px] overflow-y-auto">
              {clusterAggregates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <MapPin className="h-10 w-10 mb-2 opacity-50" />
                  <p>Geen clusters gevonden</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="sticky top-0 bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground w-12">
                        #
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                        Cluster
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                        Gebouwen
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">
                        Tickets
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {clusterAggregates.map((cluster, index) => (
                      <tr
                        key={cluster.clusterId}
                        onClick={() => handleClusterClick(cluster.clusterId)}
                        className="hover:bg-muted/30 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-2.5 text-sm text-muted-foreground font-medium">
                          {index + 1}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <MapPin
                              className="h-4 w-4 text-primary"
                              weight="duotone"
                            />
                            <span className="text-sm font-medium">
                              {cluster.clusterName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-sm text-muted-foreground">
                          {cluster.buildingCount} gebouw
                          {cluster.buildingCount !== 1 ? "en" : ""}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Badge
                            variant={
                              cluster.ticketCount >= 10
                                ? "destructive"
                                : "secondary"
                            }
                            className="font-mono"
                          >
                            {cluster.ticketCount}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Quick tip */}
        <p className="text-xs text-muted-foreground text-center">
          Klik op een rij om naar de helpdesk te gaan met de juiste filters
        </p>
      </CardContent>
    </Card>
  );
}

// Legacy export for backward compatibility - now just re-exports
export function DistributionCharts() {
  return (
    <div className="space-y-6">
      <BuildingDistributionChart />
      <CategoryDistributionChart />
      <SourceDistributionChart />
      <ClusterBuildingAnalysis />
    </div>
  );
}
